<script setup lang="ts">
import { computed } from 'vue';

import IngredientCard from '@/components/customizer/CustomizerIngredientCard.vue';
import { SkuSelection, VolumetricSelection } from '@/lib/customizers/common';
import { SkuIngredient, VolumetricIngredient } from '@/lib/customizers/ingredients';
import { isDefined } from '@/utils/isDefined';

const props = defineProps<{
  selections: (VolumetricSelection | SkuSelection | undefined)[];
  ingredients: (VolumetricIngredient | SkuIngredient)[];
  maxIngredients: number;
  mode: 'box' | 'mix';
  showPrice?: boolean;
}>();

const emit = defineEmits(['add-ingredient', 'close-modal', 'remove-ingredient']);

const quantityAdded = (productKey: string) => {
  if (props.mode === 'mix') {
    const selection = props.selections.find((ing) => ing?.productKey === productKey);

    if (selection && 'parts' in selection) {
      return selection.parts;
    }

    return 0;
  }

  const selection = props.selections.filter((ing) => ing?.productKey === productKey);

  return selection.length > 0 ? selection.length : 0;
};

const isFull = computed(() => props.selections.filter(isDefined).length === props.maxIngredients);

const handleAddIngredient = (productKey: string) => {
  emit('add-ingredient', productKey);
  if (props.mode === 'mix') {
    const inMix = props.selections.find((selection) => selection?.productKey === productKey);

    if (!inMix && isFull.value) {
      emit('close-modal');
    }
  } else if (isFull.value) emit('close-modal');
};

const handleRemoveIngredient = (productKey: string) => {
  emit('remove-ingredient', productKey);
};
</script>

<template>
  <div
    class="grid grid-cols-2 gap-4 pb-40 md:grid-cols-4 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
    data-test="ingredient-catalog"
  >
    <template v-for="ingredient in ingredients" :key="ingredient.productKey">
      <IngredientCard
        :ingredient="ingredient"
        :mode="mode"
        :quantityAdded="quantityAdded(ingredient.productKey)"
        :showPrice="showPrice"
        @add-ingredient="handleAddIngredient"
        @remove-ingredient="handleRemoveIngredient"
      />
    </template>
  </div>
</template>
